<svelte:options
  customElement={{
    tag: "oc-selection-signifier-v1",
    /*                       */
    extend: window.__components.extend(),
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import { on } from "svelte/events";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  const Host = $host();

  function findNextParentCustomElement(element: HTMLElement): HTMLElement | null {
    let parentElement = element.parentElement;
    if (!parentElement) {
      return null;
    }
    if (element.assignedSlot) {
      /*                              */
      return parentElement;
    }
    return findNextParentCustomElement(parentElement);
  }

  let checked = $state(false);
  let disabled = $state(false);
  let variant = $state("radio");

  onMount(() => {
    const parentElement = findNextParentCustomElement(Host);
    if (!parentElement) return;
    if ("singleSelection" in parentElement) {
      /*                                                               */
      variant = parentElement.singleSelection ? "radio" : "checkbox";
      if (parentElement && "checked" in parentElement && "disabled" in parentElement) {
        checked = !!parentElement.checked;
        disabled = !!parentElement.disabled;

        return on(parentElement, "oc-property-change", () => {
          checked = !!parentElement.checked;
          disabled = !!parentElement.disabled;
        });
      }
    } else if (parentElement.shadowRoot) {
      /*                                          */
      const update = (inputElement: EventTarget | HTMLElement | undefined | null): void => {
        if (inputElement instanceof HTMLInputElement) {
          /*                                                  */
          if (inputElement.parentElement !== parentElement) return;

          checked = inputElement.checked;
          disabled = inputElement.disabled;
          variant = inputElement.type;
        }
      };

      update(Array.from(parentElement.children).find((child) => child instanceof HTMLInputElement));
      /*                                         */
      return on(parentElement, "transitionstart", (ev) => update(ev.target));
    }
  });
</script>

{#if variant === "radio"}
  <div
    class="selection-signifier-radio"
    class:selection-signifier-radio--checked={checked}
    class:selection-signifier-radio--disabled={disabled}
  >
    <span class="selection-signifier-radio__circle"></span>
  </div>
{:else if variant === "checkbox"}
  <div
    class="selection-signifier-checkbox"
    class:selection-signifier-checkbox--checked={checked}
    class:selection-signifier-checkbox--disabled={disabled}
  >
    <span class="selection-signifier-checkbox__box"></span>
  </div>
{/if}

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  :global {
    :host {
      display: block;
      width: tokens.$oc-component-form-radio-button-container-size;
      height: tokens.$oc-component-form-radio-button-container-size;
    }

    /*                             */
    .selection-signifier-radio {
      position: absolute;
      width: tokens.$oc-component-form-radio-button-container-size;
      height: tokens.$oc-component-form-radio-button-container-size;

      &__circle {
        position: relative;
        display: block;
        width: tokens.$oc-component-form-radio-button-container-size;
        height: tokens.$oc-component-form-radio-button-container-size;

        outline: 1px solid tokens.$oc-component-form-default-border-color;
        border-radius: 50%;

        background-color: tokens.$oc-component-form-background-color;
      }

      /*             */
      &--disabled > .selection-signifier-radio__circle {
        outline-color: tokens.$oc-component-selection-signifier-disabled-border-color;
        background-color: tokens.$oc-component-selection-signifier-disabled-background-color;
      }

      /*            */
      &--checked > .selection-signifier-radio__circle {
        background-color: tokens.$oc-component-selection-signifier-default-background-color-checked;
      }

      &--checked > .selection-signifier-radio__circle:before {
        $center-dot: 7px;

        content: "";
        display: block;
        position: absolute;
        left: $center-dot;
        top: $center-dot;
        bottom: $center-dot;
        right: $center-dot;

        border-radius: 50%;
        background-color: tokens.$oc-component-selection-signifier-default-background-color;
      }

      /*                       */
      &--checked.selection-signifier-radio--disabled > .selection-signifier-radio__circle {
        outline: none;
        background-color: tokens.$oc-component-selection-signifier-disabled-background-color-checked;
      }

      &--checked.selection-signifier-radio--disabled > .selection-signifier-radio__circle:before {
        $center-dot: 7px;

        content: "";
        display: block;
        position: absolute;
        left: $center-dot;
        top: $center-dot;
        bottom: $center-dot;
        right: $center-dot;

        border-radius: 50%;
        background-color: tokens.$oc-component-selection-signifier-disabled-icon-color;
      }
    }

    /*                                */
    .selection-signifier-checkbox {
      position: absolute;
      width: tokens.$oc-component-form-checkbox-container-size;
      height: tokens.$oc-component-form-checkbox-container-size;

      &__box {
        position: relative;
        display: block;
        width: tokens.$oc-component-form-checkbox-container-size;
        height: tokens.$oc-component-form-checkbox-container-size;

        outline: 1px solid tokens.$oc-component-form-default-border-color;
        border-radius: tokens.$oc-component-form-checkbox-border-radius;

        background-color: tokens.$oc-component-form-background-color;
      }

      /*             */
      &--disabled > .selection-signifier-checkbox__box {
        outline-color: tokens.$oc-component-selection-signifier-disabled-border-color;
        background-color: tokens.$oc-component-selection-signifier-disabled-background-color;
      }

      /*            */
      &--checked > .selection-signifier-checkbox__box {
        background-color: tokens.$oc-component-selection-signifier-default-background-color-checked;
      }

      &--checked > .selection-signifier-checkbox__box:before {
        $center-icon: calc(50% - tokens.$oc-component-form-checkbox-icon-size / 2);

        content: "";
        display: block;
        position: absolute;
        width: tokens.$oc-component-form-checkbox-icon-size;
        height: tokens.$oc-component-form-checkbox-icon-size;
        left: $center-icon;
        top: $center-icon;
        bottom: $center-icon;
        right: $center-icon;

        mask-image: url("/assets-static/icons/pl_icon_check50.svg");
        background-color: tokens.$oc-component-selection-signifier-default-icon-color;
      }

      /*                       */
      &--checked.selection-signifier-checkbox--disabled > .selection-signifier-checkbox__box {
        outline: none;
        background-color: tokens.$oc-component-selection-signifier-disabled-background-color-checked;
      }

      &--checked.selection-signifier-checkbox--disabled
        > .selection-signifier-checkbox__box:before {
        $center-icon: calc(50% - tokens.$oc-component-form-checkbox-icon-size / 2);

        content: "";
        display: block;
        position: absolute;
        width: tokens.$oc-component-form-checkbox-icon-size;
        height: tokens.$oc-component-form-checkbox-icon-size;
        left: $center-icon;
        top: $center-icon;
        bottom: $center-icon;
        right: $center-icon;

        mask-image: url("/assets-static/icons/pl_icon_check50.svg");
        background-color: tokens.$oc-component-selection-signifier-disabled-icon-color;
      }
    }
  }
</style>
